import {
  newsList,
  categories,
  channels,
  type NewsArticle,
  type Category,
  type Channel,
} from "@/mocks/news";

export const allNews = newsList;

export function getArticleById(id: string): NewsArticle | undefined {
  return newsList.find((item) => item.id === id);
}

export function getCategoryBySlug(slug: string): Category | undefined {
  return categories.find((item) => item.slug === slug);
}

export function getChannelBySlug(slug: string): Channel | undefined {
  return channels.find((item) => item.slug === slug);
}

export function getByCategory(slug: string): NewsArticle[] {
  return newsList.filter((item) => item.categorySlug === slug);
}

export function getByChannel(slug: string): NewsArticle[] {
  return newsList.filter((item) => item.channelSlug === slug);
}

export function getFeatured(): NewsArticle[] {
  return newsList.filter((item) => item.featured);
}

export function getLatest(limit?: number): NewsArticle[] {
  const sorted = [...newsList].sort(
    (a, b) => new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime(),
  );
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getHottest(limit?: number): NewsArticle[] {
  const sorted = [...newsList].sort((a, b) => b.views + b.shares * 5 - (a.views + a.shares * 5));
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getEditorPicks(limit?: number): NewsArticle[] {
  const picks = newsList.filter((item) => item.editorPick);
  return typeof limit === "number" ? picks.slice(0, limit) : picks;
}

const timeRangeDays: Record<string, number> = {
  今日: 1,
  近三天: 3,
  近一周: 7,
  近一月: 30,
};

export function filterNews(
  source: NewsArticle[],
  options: { region?: string; timeRange?: string; tag?: string },
): NewsArticle[] {
  const { region, timeRange, tag } = options;
  const now = new Date("2026-10-14T12:00:00").getTime();
  return source.filter((item) => {
    if (region && region !== "全部" && item.region !== region) return false;
    if (tag && tag !== "全部" && !item.tags.includes(tag)) return false;
    if (timeRange && timeRange !== "全部") {
      const days = timeRangeDays[timeRange] ?? 9999;
      const diff = (now - new Date(item.publishTime).getTime()) / (1000 * 60 * 60 * 24);
      if (diff > days) return false;
    }
    return true;
  });
}

export type SortKey = "latest" | "hot";

export function sortNews(source: NewsArticle[], key: SortKey): NewsArticle[] {
  const copy = [...source];
  if (key === "hot") {
    return copy.sort((a, b) => b.views + b.shares * 5 - (a.views + a.shares * 5));
  }
  return copy.sort(
    (a, b) => new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime(),
  );
}

export function searchNews(keyword: string): NewsArticle[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return newsList.filter((item) => {
    const haystack = [item.title, item.summary, item.category, item.tags.join(" "), item.content.join(" ")]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function getRelated(article: NewsArticle, limit = 5): NewsArticle[] {
  return newsList
    .filter((item) => item.id !== article.id)
    .map((item) => {
      let score = 0;
      if (item.categorySlug === article.categorySlug) score += 3;
      if (item.channelSlug === article.channelSlug) score += 2;
      score += item.tags.filter((t) => article.tags.includes(t)).length * 2;
      return { item, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function collectTags(): string[] {
  const set = new Set<string>();
  newsList.forEach((item) => item.tags.forEach((t) => set.add(t)));
  return Array.from(set);
}

export function getPopularTags(limit = 10): string[] {
  const counter = new Map<string, number>();
  newsList.forEach((item) =>
    item.tags.forEach((t) => counter.set(t, (counter.get(t) ?? 0) + item.views)),
  );
  return Array.from(counter.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map((entry) => entry[0]);
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  const hh = `${d.getHours()}`.padStart(2, "0");
  const mm = `${d.getMinutes()}`.padStart(2, "0");
  return `${y}-${m}-${day} ${hh}:${mm}`;
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}

 export {};